import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { DiscoveryEnv, RumInitConfiguration } from 'libs/types/global';

// Publishable client-side identifiers for the discovery RUM application,
// not secrets (same class of value as the Sentry DSN in sentry.client.config.ts).
const DATADOG_RUM_APPLICATION_ID = 'db9a9e12-4980-47c0-897d-c3bd34784738';
const DATADOG_RUM_CLIENT_TOKEN = 'pube6dc936531b2b869504554080c05d555';
const DATADOG_RUM_SITE = 'datadoghq.com';

// Staging stays at 100 so every verification session records events.
const PROD_SESSION_SAMPLE_RATE = 5;
const STAGING_SESSION_SAMPLE_RATE = 100;

// RUM-initiated traces bypass backend APM sampling (which keeps ~15% of
// traces) once the backend honors the RUM sampled flag, so this rate directly
// drives backend trace volume in production. Staging stays at 100 so every
// verification session produces traces.
const PROD_TRACE_SAMPLE_RATE = 5;
const STAGING_TRACE_SAMPLE_RATE = 100;

export const buildRumConfig = (env: DiscoveryEnv): RumInitConfiguration => ({
  applicationId: DATADOG_RUM_APPLICATION_ID,
  clientToken: DATADOG_RUM_CLIENT_TOKEN,
  site: DATADOG_RUM_SITE,
  service: 'discovery',
  env: env.TIER,
  // The git sha is not available inside the container build, so the Next.js
  // buildId identifies the deployed build instead (also readable from
  // .next/BUILD_ID at sourcemap-upload time). Lowercased because Datadog tag
  // values are lowercase; the raw mixed-case buildId gets sanitized by the
  // SDK anyway (with a console warning per event), and the sourcemap upload
  // must lowercase identically or version matching breaks.
  version: window.__NEXT_DATA__?.buildId?.toLowerCase(),
  sessionSampleRate: env.TIER === 'prod' ? PROD_SESSION_SAMPLE_RATE : STAGING_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate: 0,
  traceSampleRate: env.TIER === 'prod' ? PROD_TRACE_SAMPLE_RATE : STAGING_TRACE_SAMPLE_RATE,
  defaultPrivacyLevel: 'mask',
  trackViewsManually: true,
  // Only tracecontext (Traceparent/Tracestate) is CORS-allowlisted by the
  // Cosmo Router fronting clientapi. The default propagators also send
  // x-datadog-* headers, which fail the preflight and block the GraphQL
  // request itself.
  //
  // propagateTraceBaggage must stay false for the same reason: SDK v7 also
  // injects a `baggage` header (session.id) on traced requests, which is not
  // in the router's CORS allowlist — verified live on staging 2026-08-27,
  // the preflight rejection blocks the GraphQL request entirely.
  propagateTraceBaggage: false,
  allowedTracingUrls: [
    { match: env.CLIENT_API_ENDPOINT, propagatorTypes: ['tracecontext'] },
    { match: env.SSE_CLIENT_API_ENDPOINT, propagatorTypes: ['tracecontext'] },
  ],
});

// Initializes Datadog RUM through stb-global (window.SothebysShared), which
// owns the SDK download and the OneTrust consent gate. For non-consenting
// users the SDK never loads and window.DD_RUM never exists.
export const useDatadogRum = (env: DiscoveryEnv | null): void => {
  const router = useRouter();

  useEffect(() => {
    if (!env) {
      return;
    }

    // trackViewsManually means the SDK records nothing until the first
    // startView. Consent is usually not yet readable when initDatadogRum is
    // called (OneTrust's CDN script loads after this effect), so the returned
    // promise resolves null and stb-global initializes RUM later, announcing
    // it with the datadogRumReady event. Both signals funnel through this
    // guard so the initial view starts exactly once whichever arrives first.
    let initialViewStarted = false;
    const startInitialView = (ddRum: typeof window.DD_RUM) => {
      if (!ddRum || initialViewStarted) return;
      initialViewStarted = true;
      ddRum.startView({ name: router.pathname });
    };

    const onRumReady = (event: Event) => {
      startInitialView((event as CustomEvent).detail);
    };
    window.addEventListener('datadogRumReady', onRumReady, { once: true });

    const init = (shared: typeof window.SothebysShared) => {
      // Resolves with DD_RUM when consent was already readable, or null when
      // consent is pending/declined or loading fails (see datadogRumReady
      // above for the pending case).
      void Promise.resolve(shared?.initDatadogRum?.(buildRumConfig(env))).then(startInitialView);
    };

    // The sothebysSharedReady event fires exactly once and is not replayed:
    // check for an already-present SothebysShared and register the listener
    // in the same synchronous task, or the dispatch can fire unheard.
    if (window.SothebysShared) {
      init(window.SothebysShared);
      return () => {
        window.removeEventListener('datadogRumReady', onRumReady);
      };
    }

    const onReady = (event: Event) => {
      init((event as CustomEvent).detail);
    };
    window.addEventListener('sothebysSharedReady', onReady, { once: true });

    return () => {
      window.removeEventListener('sothebysSharedReady', onReady);
      window.removeEventListener('datadogRumReady', onRumReady);
    };
  }, [env, router]);

  useEffect(() => {
    const onRouteChangeComplete = () => {
      window.DD_RUM?.startView({ name: router.pathname });
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [router]);
};
